import Artwork from "../model/artwork";
import StrapiResponse from "../model/strapiResponse";

import qs from 'qs';
import EstateContent from "../model/estateContent";
import Article from "../model/article";
import BiographyEntry from "../model/biographyEntry";

// +++++++++++ ARTWORKS ++++++++++

export async function getArtworks(
    filters: {
        typeOfObject: Array<string>,
        keywords: Array<string>,
        material: Array<string>,
        support: Array<string>,
        date: Array<string>,
        height: Array<number>,
        width: Array<number>,
    } = {
            typeOfObject: [],
            keywords: [],
            material: [],
            support: [],
            date: [],
            height: [],
            width: [],
        },
    page: number = 1,
    sort: Array<string> = ['rating:desc'],
    pageSize: number = 32,
): Promise<StrapiResponse<Artwork>> {
    if (filters.height.includes(3) && filters.height.includes(100)) filters.height = [];
    if (filters.width.includes(3) && filters.width.includes(100)) filters.width = [];
    filters.date = filters.date.map(d => '_' + d);
    const fixedMaterials: Array<string> = [];
    filters.material.forEach(val => {
        if (val === "eggTempera") fixedMaterials.push("egg tempera");
        else if (val === "organicMatter") fixedMaterials.push("organic matter");
        else fixedMaterials.push(val);
    });
    const query = qs.stringify({
        filters: {
            typeOfObject: {
                $in: filters.typeOfObject
            },
            keywords: {
                value: {
                    $eq: filters.keywords
                }
            },
            materials: {
                value: {
                    $eq: fixedMaterials
                }
            },
            support: {
                value: {
                    $eq: filters.support
                }
            },
            decade: {
                $in: filters.date
            },
            height: {
                $between: filters.height
            },
            width: {
                $between: filters.width
            },
        },
        sort: sort,
        populate: '*',
        pagination: {
            page: page,
            pageSize: pageSize
        }
    }, {
        encodeValuesOnly: true,
    });
    const url = `${process.env.REACT_APP_STRAPI_HOST}/api/artworks?${query}`;
    const res: Response = await fetch(
        url,
        {
            headers: getHeaders()
        },
    );
    if (!res.ok) {
        throw new Error(`${res.status} ${res.statusText}: Failed  to fetch ${url}`);
    }
    const json = await res.json();
    if (json.data === undefined || !(json.data instanceof Array)) {
        throw new Error(`Response contains no data: ${json}`);
    }
    const artworks: Array<Artwork> = json.data.map((aj: { attributes: any; }) => Artwork.fromJson(aj.attributes));
    return new StrapiResponse<Artwork>(
        artworks,
        json.meta.pagination.total,
        json.meta.pagination.page,
        json.meta.pagination.pageCount,
    );
}

export async function getSingleArtwork(regNum: string): Promise<Artwork | undefined> {
    const query: string = qs.stringify({
        filters: {
            regNum: {
                $eq: regNum
            }
        },
        populate: '*',
    }, {
        encodeValuesOnly: true,
    });
    const url = `${process.env.REACT_APP_STRAPI_HOST}/api/artworks?${query}`;
    const res: Response = await fetch(
        url,
        {
            headers: getHeaders()
        },
    );
    if (!res.ok) {
        throw new Error(`${res.status} ${res.statusText}: Failed  to fetch ${url}`);
    }
    const json = await res.json();
    if (json.data === undefined || !(json.data instanceof Array)) {
        throw new Error(`Response contains no data: ${json}`);
    }
    if (json.meta.pagination.total === 0) {
        return undefined;
    }
    if (json.meta.total > 1) {
        throw new Error(`Found more than one artwork: $json`);
    }
    return Artwork.fromJson(json.data.pop().attributes);
}

// +++++++++++ ESTATE ++++++++++

export async function getEstate(locale: string = 'en'): Promise<EstateContent> {
    const query: string = qs.stringify({
        locale: locale,
    }, {
        encodeValuesOnly: true,
    });
    const url = `${process.env.REACT_APP_STRAPI_HOST}/api/estate?${query}`;
    const res: Response = await fetch(
        url,
        {
            headers: getHeaders()
        },
    );
    if (!res.ok) {
        throw new Error(`${res.status} ${res.statusText}: Failed  to fetch ${url}`);
    }
    const json = await res.json();
    if (json.data === undefined || !(json.data instanceof Object)) {
        throw new Error(`Response contains no data: ${json}`);
    }
    return EstateContent.fromJson(json.data.attributes);
}

// +++++++++++ NEWS ++++++++++

export async function getArticles(locale: string = 'en', page: number = 1): Promise<StrapiResponse<Article>> {
    const query: string = qs.stringify({
        locale: locale,
        pagination: {
            page: page,
        }
    }, {
        encodeValuesOnly: true,
    });
    const url = `${process.env.REACT_APP_STRAPI_HOST}/api/articles?${query}`;
    const res: Response = await fetch(
        url,
        {
            headers: getHeaders()
        },
    );
    if (!res.ok) {
        throw new Error(`${res.status} ${res.statusText}: Failed  to fetch ${url}`);
    }
    const json = await res.json();
    if (json.data === undefined || !(json.data instanceof Array)) {
        throw new Error(`Response contains no data: ${json}`);
    }
    const artworks: Array<Article> = json.data
        .map((aj: { attributes: any; }) => Article.fromJson(aj.attributes));
    return new StrapiResponse<Article>(
        artworks,
        json.meta.pagination.total,
        json.meta.pagination.page,
        json.meta.pagination.pageCount,
    );

}

// +++++++++++ BIOGRAPHY ENTRIES ++++++++++

export async function getAllBiographyEntries(locale: string = 'en'): Promise<Array<BiographyEntry>> {
    const query: string = qs.stringify({
        locale: locale,
        sort: 'startYear:ASC',
        populate: {
            images: {
                populate: '*'
            },
        },
    }, {
        encodeValuesOnly: true,
    });
    const url = `${process.env.REACT_APP_STRAPI_HOST}/api/biography-entries?${query}`;
    const res: Response = await fetch(
        url,
        {
            headers: getHeaders()
        },
    );
    if (!res.ok) {
        throw new Error(`${res.status} ${res.statusText}: Failed  to fetch ${url}`);
    }
    const json = await res.json();
    if (json.data === undefined || !(json.data instanceof Array)) {
        throw new Error(`Response contains no data: ${json}`);
    }
    return json.data
        .map((aj: { attributes: any; }) => BiographyEntry.fromJson(aj.attributes));
}

function getHeaders(): {} {
    return {
        'Authorization': `Bearer ${process.env.REACT_APP_STRAPI_API_KEY}`,
    };
} 